import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { PhoneOff } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useTelemetryStore } from '../../stores/telemetryStore'
import { Badge } from '../ui/Badge'
import { Waveform } from '../ui/Waveform'
import { formatPhone, elapsedSince } from '../../lib/utils'

// Visual reference: screens-1.jsx::ActiveCallTile (lines 144-172).
// Each tile sits inside a dark "Active calls" accent card (Voysi flamingo
// design). Gradient avatar, live duration pill, waveform strip.

function ElapsedTimer({ startedAt }: { startedAt: number }) {
  const [display, setDisplay] = useState(() => elapsedSince(startedAt))
  useEffect(() => {
    const id = setInterval(() => setDisplay(elapsedSince(startedAt)), 1000)
    return () => clearInterval(id)
  }, [startedAt])
  return <>{display}</>
}

function avatarInitials(phone: string): string {
  const digits = phone.replace(/\D/g, '')
  return digits.slice(-2) || '??'
}

interface ActiveCallTileProps {
  phone: string
  startedAt: number
  purpose?: string
  onClick: () => void
}

function ActiveCallTile({ phone, startedAt, purpose, onClick }: ActiveCallTileProps) {
  return (
    <button
      onClick={onClick}
      className="flex flex-col gap-2.5 rounded-[14px] border border-white/10 bg-white/5 p-3.5 text-left text-white transition-colors hover:border-flamingo-300/40 hover:bg-white/[0.07]"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <div
            className="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-full text-[12px] font-bold text-white"
            style={{ background: 'linear-gradient(135deg,#FF3D84,#FF7FAC)' }}
          >
            {avatarInitials(phone)}
          </div>
          <div className="min-w-0">
            <div className="truncate text-[13.5px] font-bold">{formatPhone(phone)}</div>
            <div className="font-mono text-[11px] text-ink-400">{phone}</div>
          </div>
        </div>
        <Badge tone="success" pulse>
          <ElapsedTimer startedAt={startedAt} />
        </Badge>
      </div>
      <div
        className="flex items-center gap-2.5 rounded-[10px] px-2.5 py-2"
        style={{ background: 'rgba(255,61,132,0.08)' }}
      >
        <Waveform bars={16} height={22} />
        <div className="text-[10.5px] font-semibold tracking-[0.04em] text-flamingo-200">
          LISTENING
        </div>
      </div>
      {purpose && (
        <div className="flex items-center justify-between text-[11.5px]">
          <span className="text-ink-400">{purpose}</span>
        </div>
      )}
    </button>
  )
}

export function ActiveCallsGrid() {
  const { t } = useTranslation('calls')
  const activeCalls = useTelemetryStore((s) => s.activeCalls)
  const navigate = useNavigate()
  const entries = Object.values(activeCalls).filter((c) => c.status !== 'ended')

  if (entries.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-[14px] border border-white/10 bg-white/5 py-10 text-ink-400">
        <PhoneOff className="mb-2 h-7 w-7" />
        <p className="text-[13px]">{t('dashboard.noActiveCalls')}</p>
      </div>
    )
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {entries.map((call) => {
        const phone = call.direction === 'outbound' ? call.to_number : call.from_number
        return (
          <ActiveCallTile
            key={call.call_sid}
            phone={phone}
            startedAt={call.started_at}
            purpose={call.direction === 'outbound' ? 'Outbound · live' : 'Inbound · live'}
            onClick={() => navigate(`/app/calls/stage/${encodeURIComponent(phone)}`)}
          />
        )
      })}
    </div>
  )
}
